import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/manage_calculations/JetsonReq';

const REFRESH_INTERVAL = 60_000;

const formatTime = (value) => value ? new Date(value).toLocaleString('pl-PL') : '—';

const HistoricalStdLogs = () => {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const activeRequest = useRef(null);

    const refresh = useCallback(async () => {
        if (activeRequest.current) return;
        const controller = new AbortController();
        activeRequest.current = controller;
        setLoading(true);
        try {
            const { data } = await api.get('historical-std-logs', {
                withCredentials: true,
                signal: controller.signal,
                timeout: 15_000,
            });
            if (controller.signal.aborted) return;
            if (!data.success) {
                setError(data.message || 'Nie udało się pobrać logów.');
                return;
            }
            setSnapshot(data.payload);
            setError('');
        } catch (err) {
            if (!controller.signal.aborted) {
                setError(err.response?.status === 401 || err.response?.status === 403
                    ? 'Sesja wygasła lub nie masz dostępu do logów. Zaloguj się ponownie.'
                    : 'Nie udało się odświeżyć logów. Kolejna próba nastąpi automatycznie za minutę.');
            }
        } finally {
            if (activeRequest.current === controller) {
                activeRequest.current = null;
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        refresh();
        const timer = setInterval(refresh, REFRESH_INTERVAL);
        return () => {
            clearInterval(timer);
            activeRequest.current?.abort();
            activeRequest.current = null;
        };
    }, [refresh]);

    const lines = snapshot?.lines.slice(-10) || [];
    return (
        <section className="historical-logs-panel" aria-labelledby="historical-logs-title">
            <div className="historical-logs-header">
                <div>
                    <h2 id="historical-logs-title">Jetson · Historical Std</h2>
                    <p>Podgląd ostatnich 10 linii · odświeżanie co minutę</p>
                </div>
                <button type="button" onClick={refresh} disabled={loading}>
                    {loading ? 'Pobieranie…' : 'Odśwież teraz'}
                </button>
            </div>
            <div className="historical-logs-status" role="status">
                <span className={`historical-logs-badge${error ? ' historical-logs-badge-error' : ''}`}>
                    {error ? 'Brak aktualizacji' : loading ? 'Pobieranie…' : 'Podgląd aktywny'}
                </span>
                <span>Ostatni odczyt: {formatTime(snapshot?.fetched_at)}</span>
                <span>Zmiana pliku: {formatTime(snapshot?.modified_at)}</span>
            </div>
            {error && <p className="historical-logs-error" role="alert">
                {error}{snapshot && ' Poniżej ostatnio pobrane wpisy — mogą być nieaktualne.'}
            </p>}
            {snapshot?.truncated && <p className="historical-logs-error">
                Bardzo długie wpisy przekroczyły limit podglądu. Wyświetlono dostępną końcówkę pliku.
            </p>}
            <pre className="historical-logs-content" tabIndex={0} aria-label="Ostatnie linie logów Historical Std">
                {lines.length ? lines.join('\n') : snapshot
                    ? 'Plik logów jest pusty — brak wpisów do wyświetlenia.'
                    : loading ? 'Pobieranie logów z Jetsona…' : 'Brak pobranych logów.'}
            </pre>
            <div className="historical-logs-footer">
                <span>/var/log/jetson_app_celery/historical_std/error.log</span>
                <span>Pobrano {snapshot?.lines.length || 0} / 20 linii · wyświetlono {lines.length}</span>
            </div>
        </section>
    );
};

export default HistoricalStdLogs;
